import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { portfolioReportsAPI, trackActivity, API_BASE_URL } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePreview } from '../../contexts/PreviewContext';
import BulkDownloadModal from './BulkDownloadModal';
import PublishModal from './PublishModal';
import RtfUploadModal from '../../components/RtfUploadModal';
import ReportViewer from './ReportViewer';
import EditableContentPanel from './EditableContentPanel';
import PeriodSelector from './QuarterSelector';
import PortfolioSelector from './PortfolioSelector';
import './styles.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  ArcElement, Title, Tooltip, Legend, TimeScale, Filler
);

const PortfolioReportsApp = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { previewDomain, isPreviewMode } = usePreview();

  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isRtfLoading, setIsRtfLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState('view');
  // Brand-specific report copies for the selected quarter: [{portfolio_code, domain, ...}].
  // A brand copy overrides the Drummond version for that brand's clients.
  const [brandCopies, setBrandCopies] = useState([]);
  const [liveContent, setLiveContent] = useState(null);

  // Data date selectors — canonical per quarter, shared across all portfolios
  const [perfDate, setPerfDate] = useState(null);         // null = latest
  const [holdingsDate, setHoldingsDate] = useState(null);  // null = latest
  const [changesBaseDate, setChangesBaseDate] = useState(null);
  const [saaYear, setSaaYear] = useState(null);            // null = derive from report period
  const [availableDates, setAvailableDates] = useState({ perf_dates: [], holdings_dates: [], saa_years: [] });
  const [datesLoaded, setDatesLoaded] = useState(false);   // whether saved dates have been fetched for this quarter
  const [datesSaving, setDatesSaving] = useState(false);
  // Per-portfolio date exceptions for the quarter — {portfolio_code: {perf_date, holdings_date, changes_base_date}}.
  // Used when a portfolio's Shadow_Data is missing the canonical week (e.g. a platform missed an upload).
  const [dateOverrides, setDateOverrides] = useState({});
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  // Overflow detection state — tracks which blocks have content overflow
  const [overflowBlocks, setOverflowBlocks] = useState(new Set());

  // Inline layout editing state
  const [layoutEditMode, setLayoutEditMode] = useState(false);
  const editorRef = useRef(null);

  // Reset layout edit mode when leaving edit view
  useEffect(() => {
    if (viewMode !== 'edit') setLayoutEditMode(false);
  }, [viewMode]);

  // Bulk download state
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  const isDrummondUser = user?.is_drummond_user;

  const loadBrandCopies = useCallback(async (quarter) => {
    if (!isDrummondUser || !quarter) { setBrandCopies([]); return; }
    try {
      const r = await portfolioReportsAPI.listBrandCopies(quarter);
      setBrandCopies(r.copies || []);
    } catch (err) {
      console.error('Error loading brand copies:', err);
      setBrandCopies([]);
      setError(err.response?.data?.detail || 'Could not load brand copy list');
    }
  }, [isDrummondUser]);

  useEffect(() => { loadBrandCopies(selectedQuarter); }, [selectedQuarter, loadBrandCopies]);

  const hasBrandCopy = useCallback((code, domain) =>
    !!domain && brandCopies.some((c) => c.portfolio_code === code && c.domain === domain),
  [brandCopies]);

  // Saving while a brand is selected creates that brand's own copy the first
  // time. Confirm, because the copy stops receiving Drummond edits.
  const confirmBrandCopy = useCallback((action) => {
    if (!isDrummondUser || !selectedDomain || !selectedPortfolio) return true;
    if (hasBrandCopy(selectedPortfolio.portfolio_code, selectedDomain)) return true;
    return window.confirm(
      `${action} will create a separate ${selectedDomain} copy of this report.\n\n`
      + `From then on, edits made to the Drummond version won't reach ${selectedDomain} clients for this report. `
      + 'To change wording for every brand, cancel and edit the portfolio in the Drummond section instead.\n\n'
      + 'Create the brand copy?'
    );
  }, [isDrummondUser, selectedDomain, selectedPortfolio, hasBrandCopy]);

  const cancelledError = (what) => ({ response: { data: { detail: `${what} cancelled. No brand copy was created.` } } });

  // Effective dates for the selected portfolio — a saved per-portfolio exception
  // takes precedence over the canonical quarter dates
  const selectedOverride = selectedPortfolio ? dateOverrides[selectedPortfolio.portfolio_code] : null;
  const effPerfDate = selectedOverride?.perf_date || perfDate;
  const effHoldingsDate = selectedOverride?.holdings_date || holdingsDate;
  const effChangesBaseDate = selectedOverride?.changes_base_date || changesBaseDate;

  // Flag when the dates in use don't exist in the selected portfolio's own Shadow_Data
  const holdingsDateMismatch = !!(effHoldingsDate && availableDates.holdings_dates?.length > 0
    && !availableDates.holdings_dates.includes(effHoldingsDate));
  const changesBaseMismatch = !!(effChangesBaseDate && availableDates.holdings_dates?.length > 0
    && !availableDates.holdings_dates.includes(effChangesBaseDate));

  // Chart refs for PDF export
  const performanceChartRef = useRef(null);
  const allocationVsSaaChartRef = useRef(null);
  const activeTiltsChartRef = useRef(null);
  const allocationDonutChartRef = useRef(null);
  const historicalAllocationChartRef = useRef(null);
  const creditChartRef = useRef(null);
  const dynamicChartRefs = useRef({});

  // Track activity on mount
  useEffect(() => {
    trackActivity('app_access', 'portfolio_reports');
  }, []);

  // Load portfolio list
  useEffect(() => {
    const fetchPortfolios = async () => {
      try {
        const data = await portfolioReportsAPI.getPortfolios();
        setPortfolios(data.portfolios || []);
      } catch (err) {
        console.error('Error loading portfolios:', err);
      }
    };
    fetchPortfolios();
  }, []);

  // Load saved canonical dates and per-portfolio exceptions when quarter changes
  useEffect(() => {
    if (!selectedQuarter) return;
    let cancelled = false;
    setDatesLoaded(false);
    Promise.all([
      portfolioReportsAPI.getQuarterDates(selectedQuarter),
      portfolioReportsAPI.getQuarterDateOverrides(selectedQuarter).catch(err => {
        console.error('Error loading date overrides:', err);
        return { overrides: {} };
      }),
    ])
      .then(([result, overridesResult]) => {
        if (cancelled) return;
        if (result.dates) {
          setPerfDate(result.dates.perf_date || null);
          setHoldingsDate(result.dates.holdings_date || null);
          setChangesBaseDate(result.dates.changes_base_date || null);
          setSaaYear(result.dates.saa_year || null);
        } else {
          setPerfDate(null);
          setHoldingsDate(null);
          setChangesBaseDate(null);
          setSaaYear(null);
        }
        setDateOverrides(overridesResult.overrides || {});
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error loading quarter dates:', err);
        setPerfDate(null);
        setHoldingsDate(null);
        setChangesBaseDate(null);
        setSaaYear(null);
        setDateOverrides({});
      })
      .finally(() => { if (!cancelled) setDatesLoaded(true); });

    return () => { cancelled = true; };
  }, [selectedQuarter]);

  // Fetch available dates when portfolio changes (dropdown options only — no date resets)
  useEffect(() => {
    if (!selectedPortfolio) return;

    let cancelled = false;
    portfolioReportsAPI.getAvailableDates(selectedPortfolio.portfolio_code)
      .then(dates => {
        if (cancelled) return;
        setAvailableDates(dates);
        // Only auto-set changesBaseDate if not already set and saved dates have loaded
        if (datesLoaded && !changesBaseDate && dates.holdings_dates && dates.holdings_dates.length >= 2) {
          setChangesBaseDate(dates.holdings_dates[1]);
        }
      })
      .catch(err => {
        if (!cancelled) console.error('Error loading available dates:', err);
      });

    return () => { cancelled = true; };
  }, [selectedPortfolio]);

  // Track previous values to detect what changed
  const prevQuarterRef = useRef(selectedQuarter);
  const prevPortfolioRef = useRef(selectedPortfolio?.portfolio_code);
  const prevDomainRef = useRef(selectedDomain);
  const prevPerfDateRef = useRef(perfDate);
  const prevHoldingsDateRef = useRef(holdingsDate);
  const prevBaseDateRef = useRef(changesBaseDate);
  const prevSaaYearRef = useRef(saaYear);

  // Full data + content load — triggered by portfolio, domain, or date change
  const loadReportData = useCallback(async (quarter, cancelled) => {
    if (!selectedPortfolio || !quarter) return;

    setIsLoading(true);
    setError(null);

    try {
      const brandDomain = isDrummondUser ? selectedDomain : null;
      const result = await portfolioReportsAPI.getReportData(
        selectedPortfolio.portfolio_code,
        quarter,
        brandDomain,
        { perfDate: effPerfDate, holdingsDate: effHoldingsDate, changesBaseDate: effChangesBaseDate, saaYear }
      );

      if (cancelled?.current) return;
      if (result.success) {
        setReportData(result.data);
      } else {
        setError(result.message || 'Failed to load report data');
      }
    } catch (err) {
      if (cancelled?.current) return;
      console.error('Error loading report data:', err);
      setError(err.response?.data?.detail || 'Error loading report data');
    } finally {
      if (!cancelled?.current) setIsLoading(false);
    }
  }, [selectedPortfolio, selectedDomain, isDrummondUser, effPerfDate, effHoldingsDate, effChangesBaseDate, saaYear]);

  // Lightweight content-only load — triggered by quarter change
  const loadContentOnly = useCallback(async (quarter, cancelled) => {
    if (!selectedPortfolio || !quarter || !reportData) return;

    setError(null);

    try {
      const brandDomain = isDrummondUser ? selectedDomain : null;
      const result = await portfolioReportsAPI.getContentOnly(
        selectedPortfolio.portfolio_code,
        quarter,
        brandDomain
      );

      if (cancelled?.current) return;
      if (result.success) {
        setReportData(prev => ({
          ...prev,
          content: result.content,
          quarter: result.quarter,
          quarter_display: result.quarter_display,
        }));
      }
    } catch (err) {
      if (cancelled?.current) return;
      console.error('Error loading content:', err);
      setError(err.response?.data?.detail || 'Error loading content');
    }
  }, [selectedPortfolio, selectedDomain, isDrummondUser, reportData]);

  // Save canonical dates for the current quarter
  const handleSaveDates = useCallback(async () => {
    if (!selectedQuarter) return;
    setDatesSaving(true);
    try {
      await portfolioReportsAPI.saveQuarterDates(selectedQuarter, { perfDate, holdingsDate, changesBaseDate, saaYear });
    } catch (err) {
      console.error('Error saving quarter dates:', err);
    } finally {
      setDatesSaving(false);
    }
  }, [selectedQuarter, perfDate, holdingsDate, changesBaseDate, saaYear]);

  // Save or update the per-portfolio date exception for the selected portfolio.
  // Pass a field as null to clear just that field; fields not passed keep their value.
  const handleSetDateOverride = useCallback(async (fields) => {
    if (!selectedQuarter || !selectedPortfolio) return;
    setOverrideSaving(true);
    try {
      const current = dateOverrides[selectedPortfolio.portfolio_code] || {};
      const next = {
        perfDate: 'perfDate' in fields ? fields.perfDate : current.perf_date,
        holdingsDate: 'holdingsDate' in fields ? fields.holdingsDate : current.holdings_date,
        changesBaseDate: 'changesBaseDate' in fields ? fields.changesBaseDate : current.changes_base_date,
      };
      const result = await portfolioReportsAPI.savePortfolioDateOverride(
        selectedQuarter, selectedPortfolio.portfolio_code, next
      );
      if (result.overrides) setDateOverrides(result.overrides);
    } catch (err) {
      console.error('Error saving date override:', err);
    } finally {
      setOverrideSaving(false);
    }
  }, [selectedQuarter, selectedPortfolio, dateOverrides]);

  const handleClearDateOverride = useCallback(async () => {
    if (!selectedQuarter || !selectedPortfolio) return;
    setOverrideSaving(true);
    try {
      const result = await portfolioReportsAPI.savePortfolioDateOverride(
        selectedQuarter, selectedPortfolio.portfolio_code, {}
      );
      if (result.overrides) setDateOverrides(result.overrides);
    } catch (err) {
      console.error('Error clearing date override:', err);
    } finally {
      setOverrideSaving(false);
    }
  }, [selectedQuarter, selectedPortfolio]);

  // Decide whether to do full load or content-only load
  useEffect(() => {
    if (!selectedPortfolio || !selectedQuarter) return;

    const cancelled = { current: false };

    const portfolioChanged = prevPortfolioRef.current !== selectedPortfolio.portfolio_code;
    const domainChanged = prevDomainRef.current !== selectedDomain;
    const quarterChanged = prevQuarterRef.current !== selectedQuarter;
    const dateChanged = prevPerfDateRef.current !== effPerfDate
      || prevHoldingsDateRef.current !== effHoldingsDate
      || prevBaseDateRef.current !== effChangesBaseDate
      || prevSaaYearRef.current !== saaYear;

    // Update refs
    prevPortfolioRef.current = selectedPortfolio.portfolio_code;
    prevDomainRef.current = selectedDomain;
    prevQuarterRef.current = selectedQuarter;
    prevPerfDateRef.current = effPerfDate;
    prevHoldingsDateRef.current = effHoldingsDate;
    prevBaseDateRef.current = effChangesBaseDate;
    prevSaaYearRef.current = saaYear;

    if (portfolioChanged || domainChanged || dateChanged || !reportData) {
      // Full reload needed
      loadReportData(selectedQuarter, cancelled);
    } else if (quarterChanged) {
      // Quarter-only change — lightweight content swap
      loadContentOnly(selectedQuarter, cancelled);
    }

    return () => { cancelled.current = true; };
  }, [selectedPortfolio, selectedQuarter, selectedDomain, effPerfDate, effHoldingsDate, effChangesBaseDate, saaYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Export chart images as high-res base64 PNGs (3x DPR for print quality)
  const exportChartImages = useCallback(() => {
    const images = {};
    const EXPORT_DPR = 3;
    const refs = {
      performance: performanceChartRef,
      allocation_vs_saa: allocationVsSaaChartRef,
      active_tilts: activeTiltsChartRef,
      allocation_donut: allocationDonutChartRef,
      historical_allocation: historicalAllocationChartRef,
      credit_exposure: creditChartRef,
    };

    for (const [key, ref] of Object.entries(refs)) {
      const chartInstance = ref.current;
      if (!chartInstance) continue;
      try {
        const origDPR = chartInstance.options.devicePixelRatio;
        chartInstance.options.devicePixelRatio = EXPORT_DPR;
        chartInstance.resize();
        chartInstance.update('none');
        images[key] = chartInstance.toBase64Image('image/png');
        chartInstance.options.devicePixelRatio = origDPR;
        chartInstance.resize();
        chartInstance.update('none');
      } catch (e) {
        console.warn(`Could not export ${key} chart:`, e);
      }
    }

    // Export dynamic template chart images
    for (const [blockId, chartRef] of Object.entries(dynamicChartRefs.current)) {
      if (!chartRef) continue;
      try {
        const origDPR = chartRef.options.devicePixelRatio;
        chartRef.options.devicePixelRatio = EXPORT_DPR;
        chartRef.resize();
        chartRef.update('none');
        images[blockId] = chartRef.toBase64Image('image/png');
        chartRef.options.devicePixelRatio = origDPR;
        chartRef.resize();
        chartRef.update('none');
      } catch (e) {
        console.warn(`Could not export dynamic chart ${blockId}:`, e);
      }
    }

    return Object.keys(images).length > 0 ? images : null;
  }, []);

  // Fetch an image URL as an inline data URL (handles CORS via credentials)
  const fetchImageAsDataUrl = useCallback(async (url) => {
    const resp = await fetch(url, { credentials: 'include' });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Capture the rendered report DOM as a self-contained HTML document for WYSIWYG PDF.
  // Must be called while in VIEW mode — no editing UI exists in the DOM.
  // Converts <img> to inline data URLs first, then <canvas> charts to high-res <img>.
  const captureReportHTML = useCallback(async () => {
    const reportEl = document.querySelector('.report-pages');
    if (!reportEl) return null;

    const clone = reportEl.cloneNode(true);

    // Step 1: Convert existing <img> elements to inline data URLs BEFORE canvas
    // conversion so that index-based matching between live and clone is correct.
    const liveImages = reportEl.querySelectorAll('img');
    const cloneImages = clone.querySelectorAll('img');
    const imgPromises = [];
    liveImages.forEach((img, i) => {
      if (!cloneImages[i]) return;
      const cloneImg = cloneImages[i];

      // Use the original branded URL if onError has already swapped to fallback
      const originalSrc = img.getAttribute('data-original-src');
      let fetchSrc = originalSrc || img.src;

      // Strip API_BASE_URL so fetch goes through same-origin (Vite proxy in dev)
      if (API_BASE_URL && fetchSrc.startsWith(API_BASE_URL)) {
        fetchSrc = fetchSrc.slice(API_BASE_URL.length);
      }

      // Try synchronous canvas draw first (works for same-origin images).
      // Skip if we have an original src that differs from current (onError fired).
      if (!originalSrc || originalSrc === img.src) {
        try {
          if (img.complete && img.naturalWidth > 0) {
            const c = document.createElement('canvas');
            c.width = img.naturalWidth;
            c.height = img.naturalHeight;
            c.getContext('2d').drawImage(img, 0, 0);
            cloneImg.src = c.toDataURL('image/png');
            return; // success
          }
        } catch (e) { /* CORS — fall through to async fetch */ }
      }

      // Async fallback: fetch via same-origin proxy
      if (fetchSrc && !fetchSrc.startsWith('data:')) {
        imgPromises.push(
          fetchImageAsDataUrl(fetchSrc)
            .then(dataUrl => { cloneImg.src = dataUrl; })
            .catch(() => {
              // Last resort: try the fallback logo
              return fetchImageAsDataUrl('/logo-name.png')
                .then(dataUrl => { cloneImg.src = dataUrl; })
                .catch(() => { /* give up */ });
            })
        );
      }
    });
    await Promise.all(imgPromises);

    // Step 2: Convert canvas charts to high-DPI images, marked with data-chart-img
    const liveCanvases = reportEl.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    liveCanvases.forEach((canvas, i) => {
      try {
        const chartInstance = ChartJS.getChart(canvas);
        let dataUrl;
        if (chartInstance) {
          const origDPR = chartInstance.options.devicePixelRatio;
          const origAnim = chartInstance.options.animation;
          // Charts may still be animating (the download flips edit->view one
          // frame before capture). While the animator runs, resize() DEFERS the
          // retina rescale and update('none') doesn't draw synchronously — so
          // toDataURL would grab the stale 1x canvas (fuzzy PDF charts). Stop
          // the animator and disable animation to force a synchronous 3x render.
          chartInstance.options.animation = false;
          chartInstance.options.devicePixelRatio = 3;
          chartInstance.stop();
          chartInstance.resize();
          chartInstance.update('none');
          dataUrl = canvas.toDataURL('image/png', 1.0);
          if (canvas.width < canvas.clientWidth * 2) {
            console.warn('[pdf-capture] high-DPI re-render did not apply for', canvas);
          }
          chartInstance.options.devicePixelRatio = origDPR;
          chartInstance.options.animation = origAnim;
          chartInstance.resize();
          chartInstance.update('none');
        } else {
          dataUrl = canvas.toDataURL('image/png', 1.0);
        }
        const img = document.createElement('img');
        img.src = dataUrl;
        img.setAttribute('data-chart-img', 'true');
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.display = 'block';
        if (cloneCanvases[i]?.parentNode) {
          cloneCanvases[i].parentNode.replaceChild(img, cloneCanvases[i]);
        }
      } catch (e) {
        console.warn('Could not convert canvas to image:', e);
      }
    });

    // Step 3: Collect CSS from each stylesheet into separate <style> blocks.
    // One block per sheet prevents a parse error in one sheet from breaking all CSS.
    // Skip @import/@charset/@namespace rules that are invalid mid-document.
    const styleBlocks = [];
    for (const sheet of document.styleSheets) {
      try {
        const rules = [];
        for (const rule of sheet.cssRules) {
          if (rule.type === CSSRule.IMPORT_RULE ||
              rule.type === CSSRule.CHARSET_RULE ||
              rule.type === CSSRule.NAMESPACE_RULE) continue;
          rules.push(rule.cssText);
        }
        if (rules.length > 0) {
          styleBlocks.push(`<style>${rules.join('\n')}</style>`);
        }
      } catch (e) { /* CORS — skip */ }
    }

    // Step 4: Inject CSS variable overrides from inline style as a <style> block
    // so branded colours cascade correctly in Playwright rendering.
    const inlineStyle = reportEl.getAttribute('style') || '';
    const varOverrides = inlineStyle
      .split(';')
      .filter(s => s.trim().startsWith('--'))
      .join('; ');
    if (varOverrides) {
      styleBlocks.push(`<style>.report-pages { ${varOverrides} }</style>`);
    }

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
${styleBlocks.join('\n')}
</head>
<body class="print-capture">
<div class="portfolio-reports-app report-pages print-capture-pages" style="${inlineStyle.replace(/"/g, '&quot;')}">
${clone.innerHTML}
</div>
</body>
</html>`;
  }, [fetchImageAsDataUrl]);

  // Download PDF — temporarily switches to view mode for a clean DOM capture
  const handleDownloadPdf = useCallback(async () => {
    if (!selectedPortfolio || !selectedQuarter) return;

    setIsPdfLoading(true);
    try {
      // Ensure we capture from view mode (no editing UI in DOM)
      const wasEditing = viewMode === 'edit';
      if (wasEditing) {
        flushSync(() => setViewMode('view'));
        await new Promise(r => requestAnimationFrame(r));
      }

      const htmlContent = await captureReportHTML();
      const chartImages = htmlContent ? null : exportChartImages();
      const brandDomain = isDrummondUser ? selectedDomain : null;

      // Restore edit mode before the async download call
      if (wasEditing) setViewMode('edit');

      const response = await portfolioReportsAPI.downloadPdf(
        selectedPortfolio.portfolio_code,
        selectedQuarter,
        chartImages,
        brandDomain,
        { perfDate: effPerfDate, holdingsDate: effHoldingsDate, changesBaseDate: effChangesBaseDate, saaYear },
        htmlContent
      );

      // Download the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `${selectedQuarter}_${selectedPortfolio.portfolio_code}_Report.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setError('Error generating PDF');
    } finally {
      setIsPdfLoading(false);
    }
  }, [selectedPortfolio, selectedQuarter, selectedDomain, isDrummondUser, captureReportHTML, exportChartImages, effPerfDate, effHoldingsDate, effChangesBaseDate, saaYear, viewMode]);

  // Editable text-only Word (RTF) export of the commentary — Drummond staff only.
  const handleDownloadWord = useCallback(async () => {
    if (!selectedPortfolio || !selectedQuarter) return;
    setIsRtfLoading(true);
    try {
      const brandDomain = isDrummondUser ? selectedDomain : null;
      const response = await portfolioReportsAPI.downloadRtf(
        selectedPortfolio.portfolio_code,
        selectedQuarter,
        brandDomain
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : `${selectedQuarter}_${selectedPortfolio.portfolio_code}_Quarterly_Report_text.rtf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading Word:', err);
      setError('Error generating Word document');
    } finally {
      setIsRtfLoading(false);
    }
  }, [selectedPortfolio, selectedQuarter, selectedDomain, isDrummondUser]);

  // Handle content save — Drummond users editing a branded portfolio must
  // route the save to that brand's domain row, not the _default row.
  const handleContentSave = useCallback(async (content) => {
    if (!selectedPortfolio || !selectedQuarter) return;
    if (!confirmBrandCopy('Saving')) throw cancelledError('Save');
    try {
      const brandDomain = isDrummondUser ? selectedDomain : undefined;
      await portfolioReportsAPI.saveContent(
        selectedPortfolio.portfolio_code,
        selectedQuarter,
        content,
        brandDomain || '_default'
      );
      // Reload just the content to reflect saved changes
      loadContentOnly(selectedQuarter);
      if (brandDomain) loadBrandCopies(selectedQuarter);
    } catch (err) {
      console.error('Error saving content:', err);
      throw err;
    }
  }, [selectedPortfolio, selectedQuarter, selectedDomain, isDrummondUser, loadContentOnly, confirmBrandCopy, loadBrandCopies]);

  // Delete this brand's copy so its clients see the Drummond version again.
  // The deleted copy is kept in the report's version history.
  const [isResetting, setIsResetting] = useState(false);
  const handleResetToDrummond = useCallback(async () => {
    if (!selectedPortfolio || !selectedQuarter || !selectedDomain) return;
    if (!window.confirm(
      `Delete the ${selectedDomain} copy of this report?\n\n`
      + `${selectedDomain} clients will see the Drummond version again, including future Drummond edits. `
      + 'The deleted copy is kept in version history and can be restored.'
    )) return;
    setIsResetting(true);
    try {
      await portfolioReportsAPI.deleteContent(selectedPortfolio.portfolio_code, selectedQuarter, selectedDomain);
      await loadBrandCopies(selectedQuarter);
      loadContentOnly(selectedQuarter);
    } catch (err) {
      console.error('Error resetting brand copy:', err);
      setError(err.response?.data?.detail || 'Could not reset to the Drummond version');
    } finally {
      setIsResetting(false);
    }
  }, [selectedPortfolio, selectedQuarter, selectedDomain, loadBrandCopies, loadContentOnly]);

  // ---- Bulk Download ----

  return (
    <div
      className={`portfolio-reports-app ${theme}`}
      style={{
        '--primary-navy': reportData?.branding?.primary_color || '#072040',
        '--secondary-gold': reportData?.branding?.secondary_color || '#f0a500',
      }}
    >
      {/* Sidebar */}
      <div className="reports-sidebar open">
        <div className="sidebar-content">
          <h3 className="sidebar-title">Portfolio Reports</h3>

          {/* Period selector — canonical for all portfolios */}
          <PeriodSelector
            selectedQuarter={selectedQuarter}
            onSelectQuarter={setSelectedQuarter}
            reportingFrequency={selectedPortfolio?.reporting_frequency || 'quarterly'}
          />

          {/* Portfolio list — grouped by brand for Drummond users */}
          <PortfolioSelector
            selectedPortfolio={selectedPortfolio}
            selectedDomain={selectedDomain}
            onSelectPortfolio={(portfolio, domain) => {
              setSelectedPortfolio(portfolio);
              setSelectedDomain(domain);
            }}
            isDrummondUser={isDrummondUser}
            quarter={selectedQuarter}
            brandCopies={brandCopies}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="reports-main">
        {/* Toolbar */}
        <div className="reports-toolbar">
          <div className="toolbar-left">
            {reportData && (
              <h2 className="report-title">
                {reportData.portfolio_name} — {reportData.quarter_display}
              </h2>
            )}
          </div>

          {/* Date selectors — only visible in edit mode. Dates are canonical per
              quarter and shared across portfolios, so the toolbar stays visible
              even when the selected portfolio has no dates of its own (e.g. no
              audited performance rows); a persisted date missing from this
              portfolio's options is kept as an extra option so it stays visible. */}
          {viewMode === 'edit' && isDrummondUser && reportData && (
            <div className="toolbar-dates">
              <label className="date-selector">
                <span>Performance Date</span>
                <select
                  value={perfDate || ''}
                  onChange={e => setPerfDate(e.target.value || null)}
                >
                  <option value="">
                    Latest{availableDates.perf_dates[0] ? ` (${availableDates.perf_dates[0]})` : ''}
                  </option>
                  {perfDate && !availableDates.perf_dates.includes(perfDate) && (
                    <option value={perfDate} disabled>{perfDate} — no data for this portfolio</option>
                  )}
                  {availableDates.perf_dates.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
              <label className="date-selector">
                <span>Holdings Date</span>
                <select
                  value={holdingsDate || ''}
                  onChange={e => setHoldingsDate(e.target.value || null)}
                >
                  <option value="">
                    Latest{availableDates.holdings_dates[0] ? ` (${availableDates.holdings_dates[0]})` : ''}
                  </option>
                  {holdingsDate && !availableDates.holdings_dates.includes(holdingsDate) && (
                    <option value={holdingsDate} disabled>{holdingsDate} — no data for this portfolio</option>
                  )}
                  {availableDates.holdings_dates.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
              <label className="date-selector">
                <span>Changes From</span>
                <select
                  value={changesBaseDate || ''}
                  onChange={e => setChangesBaseDate(e.target.value || null)}
                >
                  <option value="">None</option>
                  {changesBaseDate && !availableDates.holdings_dates.includes(changesBaseDate) && (
                    <option value={changesBaseDate} disabled>{changesBaseDate} — no data for this portfolio</option>
                  )}
                  {availableDates.holdings_dates.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </label>
              <label className="date-selector">
                <span>SAA Year</span>
                <select
                  value={saaYear || ''}
                  onChange={e => setSaaYear(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">
                    {reportData?.saa_year ? `Auto (FY${reportData.saa_year})` : 'Auto (from period)'}
                  </option>
                  {saaYear && !(availableDates.saa_years || []).some(y => y.value === saaYear) && (
                    <option value={saaYear}>{`FY${saaYear}`}</option>
                  )}
                  {(availableDates.saa_years || []).map(y => (
                    <option key={y.value} value={y.value}>{y.label}</option>
                  ))}
                </select>
              </label>
              {isDrummondUser && (
                <button
                  className="btn-save-dates"
                  onClick={handleSaveDates}
                  disabled={datesSaving}
                >
                  {datesSaving ? 'Saving...' : 'Save Dates'}
                </button>
              )}
            </div>
          )}

          <div className="toolbar-right">
            {isDrummondUser && reportData && (
              <button
                className={`btn-mode ${viewMode === 'edit' ? 'active' : ''}`}
                onClick={() => setViewMode(viewMode === 'edit' ? 'view' : 'edit')}
              >
                {viewMode === 'edit' ? 'View Mode' : 'Edit Mode'}
              </button>
            )}
            {isDrummondUser && reportData && selectedDomain && selectedPortfolio
              && hasBrandCopy(selectedPortfolio.portfolio_code, selectedDomain) && (
              <button
                className="btn-download"
                onClick={handleResetToDrummond}
                disabled={isResetting}
                title={`Delete the ${selectedDomain} copy so its clients see the Drummond version`}
              >
                {isResetting ? 'Resetting...' : 'Reset to Drummond version'}
              </button>
            )}
            {reportData && (
              <button
                className="btn-download"
                onClick={handleDownloadPdf}
                disabled={isPdfLoading}
              >
                {isPdfLoading ? 'Generating...' : 'Download PDF'}
              </button>
            )}
            {isDrummondUser && reportData && (
              <button
                className="btn-download"
                onClick={handleDownloadWord}
                disabled={isRtfLoading}
                title="Editable text-only Word file for editing commentary and pasting back in"
              >
                {isRtfLoading ? 'Generating...' : 'Download Word (text)'}
              </button>
            )}
            {isDrummondUser && reportData && (
              <button
                className="btn-download"
                onClick={() => setIsUploadModalOpen(true)}
                title="Upload an edited Word (RTF) file to repopulate the report text"
              >
                Upload Word (text)
              </button>
            )}
            {selectedQuarter && (
              <button
                className="btn-download"
                onClick={() => setIsBulkModalOpen(true)}
              >
                Bulk Download
              </button>
            )}
            {isDrummondUser && selectedQuarter && (
              <button
                className="btn-publish"
                onClick={() => setIsPublishModalOpen(true)}
              >
                Manage Publishing
              </button>
            )}
          </div>
        </div>

        {/* Per-portfolio date exception — flags when the canonical dates don't
            exist in this portfolio's Shadow_Data and lets the user pick one of
            the portfolio's actual dates instead. Saved per (quarter, portfolio). */}
        {isDrummondUser && reportData && selectedPortfolio
          && (holdingsDateMismatch || changesBaseMismatch || selectedOverride) && (
          <div className="date-exception-bar">
            <span className="date-exception-flag">
              {selectedOverride
                ? `Date exception active for ${selectedPortfolio.portfolio_code}`
                : `${selectedPortfolio.portfolio_code} has no holdings data for ${[
                    holdingsDateMismatch ? holdingsDate : null,
                    changesBaseMismatch && changesBaseDate !== holdingsDate ? changesBaseDate : null,
                  ].filter(Boolean).join(' or ')} — set an exception date`}
            </span>
            <label className="date-selector">
              <span>Holdings Date</span>
              <select
                value={selectedOverride?.holdings_date || ''}
                onChange={e => handleSetDateOverride({ holdingsDate: e.target.value || null })}
                disabled={overrideSaving}
              >
                <option value="">Canonical ({holdingsDate || 'latest'})</option>
                {availableDates.holdings_dates.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            <label className="date-selector">
              <span>Changes From</span>
              <select
                value={selectedOverride?.changes_base_date || ''}
                onChange={e => handleSetDateOverride({ changesBaseDate: e.target.value || null })}
                disabled={overrideSaving}
              >
                <option value="">Canonical ({changesBaseDate || 'none'})</option>
                {availableDates.holdings_dates.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
            {selectedOverride && (
              <button
                className="btn-clear-exception"
                onClick={handleClearDateOverride}
                disabled={overrideSaving}
              >
                Clear exception
              </button>
            )}
            {overrideSaving && <span className="date-exception-saving">Saving…</span>}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="error-banner">{error}</div>
        )}

        {/* Brand copy status for the selected report */}
        {isDrummondUser && reportData && selectedPortfolio && (() => {
          const code = selectedPortfolio.portfolio_code;
          if (selectedDomain) {
            return hasBrandCopy(code, selectedDomain) ? (
              <div className="brand-copy-notice">
                {selectedDomain} has its own copy of this report. Edits made to the Drummond version don't reach it.
              </div>
            ) : (
              <div className="brand-copy-notice muted">
                {selectedDomain} uses the Drummond version of this report. Saving here creates a separate {selectedDomain} copy.
              </div>
            );
          }
          const copies = brandCopies.filter((c) => c.portfolio_code === code);
          return copies.length ? (
            <div className="brand-copy-notice">
              {copies.length === 1 ? 'This brand has' : 'These brands have'} their own copy of this report and won't see
              edits made here: {copies.map((c) => c.domain).join(', ')}.
            </div>
          ) : null;
        })()}

        {/* Loading */}
        {isLoading && (
          <div className="loading-container">
            <div className="loading-spinner" />
            <p>Loading report data...</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !reportData && !error && (
          <div className="empty-state">
            <p>Select a portfolio and period to view the report.</p>
          </div>
        )}

        {/* Report content — split view when editing */}
        {!isLoading && reportData && (
          <div className={`fact-sheet-wrapper ${viewMode === 'edit' ? 'split-view' : ''}`}>
            <div className="fact-sheet-content">
              <ReportViewer
                data={viewMode === 'edit' && liveContent
                  ? { ...reportData, content: { ...reportData.content, ...liveContent } }
                  : reportData}
                performanceChartRef={performanceChartRef}
                allocationVsSaaChartRef={allocationVsSaaChartRef}
                activeTiltsChartRef={activeTiltsChartRef}
                allocationDonutChartRef={allocationDonutChartRef}
                historicalAllocationChartRef={historicalAllocationChartRef}
                creditChartRef={creditChartRef}
                dynamicChartRefs={dynamicChartRefs}
                onOverflowChange={setOverflowBlocks}
                layoutEditMode={layoutEditMode}
                onBlockChange={(pageIndex, blockId, updated) => {
                  editorRef.current?.handleBlockChange(pageIndex, blockId, updated);
                }}
                onBlocksChange={(pageIndex, updates) => {
                  editorRef.current?.handleBlocksChange(pageIndex, updates);
                }}
                onRemoveBlock={(blockId) => {
                  editorRef.current?.handleRemoveBlock(blockId);
                }}
                onAddBlock={(pageIndex) => {
                  editorRef.current?.handleAddBlock(pageIndex);
                }}
                overflowBlocks={overflowBlocks}
                customBlocks={liveContent?.custom_blocks || reportData.content?.custom_blocks}
                editMode={viewMode === 'edit' && isDrummondUser}
                onTextChange={(key, html) => {
                  editorRef.current?.setField?.(key, html);
                }}
                onCustomTextChange={(blockId, html) => {
                  editorRef.current?.setCustomContent?.(blockId, html);
                }}
                onHeadingChange={(blockId, text) => {
                  editorRef.current?.setHeading?.(blockId, text);
                }}
                onHeadingStyleChange={(blockId, styles) => {
                  editorRef.current?.setHeadingStyle?.(blockId, styles);
                }}
                onBlockDefChange={(blockId, blockDef) => {
                  editorRef.current?.setBlockDef?.(blockId, blockDef);
                }}
              />
            </div>

            {viewMode === 'edit' && isDrummondUser && (
              <div className="factsheet-right-panel">
                <div className="edit-panel-content">
                  <EditableContentPanel
                    ref={editorRef}
                    content={reportData.content}
                    portfolioCode={reportData.portfolio_code}
                    portfolioName={reportData.portfolio_name}
                    quarter={selectedQuarter}
                    branding={reportData.branding}
                    onSave={handleContentSave}
                    onChange={setLiveContent}
                    portfolios={portfolios}
                    domain={selectedDomain}
                    overflowBlocks={overflowBlocks}
                    onLayoutModeChange={setLayoutEditMode}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <BulkDownloadModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        isDrummondUser={isDrummondUser}
        quarter={selectedQuarter}
        perfDate={perfDate}
        holdingsDate={holdingsDate}
        changesBaseDate={changesBaseDate}
        saaYear={saaYear}
        dateOverrides={dateOverrides}
      />

      <PublishModal
        isOpen={isPublishModalOpen}
        onClose={(saved) => setIsPublishModalOpen(false)}
        quarter={selectedQuarter}
        isDrummondUser={isDrummondUser}
      />

      <RtfUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        title="Upload edited Word file"
        parseFn={(f) => portfolioReportsAPI.parseRtf(
          selectedPortfolio.portfolio_code, selectedQuarter, f,
          isDrummondUser ? selectedDomain : null)}
        applyFn={(f) => {
          if (!confirmBrandCopy('Uploading this file')) return Promise.reject(cancelledError('Upload'));
          return portfolioReportsAPI.applyRtf(
            selectedPortfolio.portfolio_code, selectedQuarter, f,
            isDrummondUser ? selectedDomain : null);
        }}
        onApplied={() => { loadContentOnly(selectedQuarter); loadBrandCopies(selectedQuarter); }}
      />
    </div>
  );
};

export default PortfolioReportsApp;
